import {useMutation, UseMutationResult, useQuery, useQueryClient} from '@tanstack/react-query';
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {QUERY_KEYS} from '../../../../utils/queryKeys';
import ConfirmModal from '../../../../incase-domain/components/modals/confirmModal/ConfirmModal';
import {IPresentation} from '../../../../incase-domain/presentation';
import {fetchSharedPresentationsPaginated, removeSharedPresentation} from '../../../../service/presentation';
import {IUpdateError} from '../../../../store/actions/types/error.types';
import {loggedInUserSelector} from '../../../../store/selectors/auth.selectors';
import PresentationsPageHeader from '../../../components/presentationList/PresentationsPageHeader';
import LoadingSpinnerComponent from '../../../components/LoadingSpinnerComponent';
import CreatePresentationLinksModals from '../../../components/presentationList/presentationLinks/CreatePresentationLinksModals';
import SharedPresentationListView from '../../../components/presentationList/SharedPresentationListView';
import {useSharedPresentationsListSearchParams} from '../../../components/presentationList/sharedPresentationsListSearchParams';

export default function EditorSharedPresentationsPage(): JSX.Element {
    const {t} = useTranslation();
    const client = useQueryClient();

    const loggedInUser = useSelector(loggedInUserSelector);

    const [deletePresentations, setDeletePresentations] = useState(false);
    const [presentationToDelete, setPresentationToDelete] = useState<IPresentation[]>([]);
    const [selectedPresentationForLinks, setSelectedPresentationForLinks] = useState<IPresentation>();
    const [isLinksModalOpen, setIsLinksModalOpen] = useState(false);

    const {listParams, setListParams} = useSharedPresentationsListSearchParams();

    const sharedPresentationList = useQuery(
        QUERY_KEYS.sharedPresentation.list(listParams),
        () => fetchSharedPresentationsPaginated(listParams.page, listParams.size, listParams.search, listParams.sortBy),
        {keepPreviousData: true}
    );

    function onDeletePageChangeHandler(): void {
        if (!sharedPresentationList.data) {
            return;
        }

        const newElCount = sharedPresentationList.data.numberOfElements - 1;
        const newMaxPage = Math.max(Math.ceil(newElCount / sharedPresentationList.data.pageSize), 1);

        if (listParams.page > newMaxPage) {
            setListParams({page: newMaxPage});
        }
    }

    const deleteSharedPresentation: UseMutationResult<IPresentation & {errors: Record<string, string[]>}, IUpdateError, IPresentation> =
        useMutation((presentation) => removeSharedPresentation(presentation._id, loggedInUser?.email as string), {
            onSuccess: () => {
                sharedPresentationList.refetch();
                setPresentationToDelete([]);
                onDeletePageChangeHandler();
                client.invalidateQueries({queryKey: QUERY_KEYS.theme.lists()});
            },
        });

    const openLinksModal = (presentation: IPresentation): void => {
        setSelectedPresentationForLinks(presentation);
        setIsLinksModalOpen(true);
    };

    return (
        <>
            <PresentationsPageHeader view="shared" />
            {sharedPresentationList.isLoading ? (
                <LoadingSpinnerComponent />
            ) : (
                <>
                    {sharedPresentationList.data?.content.length ? (
                        <SharedPresentationListView
                            presentationList={sharedPresentationList.data}
                            listParams={listParams}
                            setListParams={setListParams}
                            setDeletePresentations={setDeletePresentations}
                            setPresentationToDelete={setPresentationToDelete}
                            setSelectedPresentationForLinks={openLinksModal}
                            loading={sharedPresentationList.isFetching || sharedPresentationList.isLoading}
                        />
                    ) : (
                        <div className="c-errorMessage">{t('homePage.noSharedPresentations')}</div>
                    )}
                </>
            )}
            {selectedPresentationForLinks && (
                <CreatePresentationLinksModals
                    open={isLinksModalOpen}
                    closeModal={() => setIsLinksModalOpen(false)}
                    presentation={selectedPresentationForLinks}
                />
            )}
            <ConfirmModal
                message={`${t('editorPage.deletePresentationModal.deletePresentationConfirmation')} ${presentationToDelete[0]?.name}?`}
                isOpen={deletePresentations}
                color="error"
                onCancel={() => {
                    setPresentationToDelete([]);
                    setDeletePresentations(false);
                }}
                onConfirm={() => {
                    deleteSharedPresentation.mutate(presentationToDelete[0]);
                    setDeletePresentations(false);
                }}
                showCancel
                showConfirm
                cancelText={`${t('common.cancel')}`}
                confirmText={`${t('common.confirm')}`}
            />
        </>
    );
}
